import { Controller } from "@hotwired/stimulus";
import { dsConfirm } from "../../utils/ds_confirm";

/**
 * School Setting Controller
 *
 * Gère les interactions pour les paramètres d'école
 */
export default class extends Controller {
  static targets = [
    "settingKey",
    "settingHelp",
    "settingValue",
    "form",
    "submitButton",
    "settingItem",
    "category",
  ];

  connect() {
    console.log("SchoolSettingController connected");
    this.initializeHelpText();
    this.addEventListeners();
  }

  disconnect() {
    this.removeEventListeners();
  }

  initializeHelpText() {
    if (this.hasSettingKeyTarget && this.hasSettingHelpTarget) {
      const selectedKey = this.settingKeyTarget.value;
      if (selectedKey) {
        this.updateHelpText(selectedKey);
      }
    }
  }

  addEventListeners() {
    if (this.hasSettingKeyTarget) {
      this.settingKeyTarget.addEventListener(
        "change",
        this.handleSettingKeyChange.bind(this),
      );
    }

    if (this.hasFormTarget) {
      this.formTarget.addEventListener(
        "change",
        this.handleFormChange.bind(this),
      );
    }
  }

  removeEventListeners() {
    if (this.hasSettingKeyTarget) {
      this.settingKeyTarget.removeEventListener(
        "change",
        this.handleSettingKeyChange.bind(this),
      );
    }

    if (this.hasFormTarget) {
      this.formTarget.removeEventListener(
        "change",
        this.handleFormChange.bind(this),
      );
    }
  }

  handleSettingKeyChange(event) {
    const selectedKey = event.target.value;
    this.updateHelpText(selectedKey);

    // Mettre à jour le champ de valeur avec un exemple ou une valeur par défaut
    this.updateDefaultValue(selectedKey);
  }

  handleFormChange() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !this.isFormValid();
    }
  }

  isFormValid() {
    if (this.hasSettingKeyTarget && this.hasSettingValueTarget) {
      return this.settingKeyTarget.value && this.settingValueTarget.value;
    }
    return true;
  }

  updateHelpText(key) {
    if (this.hasSettingHelpTarget) {
      // Récupérer le texte d'aide depuis les attributs de données
      const helpTexts = this.element.dataset.helpTexts
        ? JSON.parse(this.element.dataset.helpTexts)
        : {};
      const helpText = helpTexts[key] || "";
      this.settingHelpTarget.textContent = helpText;
    }
  }

  updateDefaultValue(key) {
    if (this.hasSettingValueTarget) {
      // Définir des valeurs par défaut pour certains types de paramètres
      const defaultValues = {
        leave_approval_levels: "1",
        leave_approval_threshold: "5",
        leave_auto_approval: "false",
        leave_max_consecutive_days: "30",
        school_working_days: "1,2,3,4,5",
        school_working_hours: "08:00-16:00",
        school_timezone: "Europe/Paris",
        notification_email_enabled: "true",
        notification_sms_enabled: "false",
        notification_push_enabled: "false",
      };

      // Ne mettre à jour que si le champ est vide
      if (!this.settingValueTarget.value && defaultValues[key]) {
        this.settingValueTarget.value = defaultValues[key];
      }

      // Ajuster le type de champ en fonction du paramètre
      this.adjustValueFieldType(key);
    }
  }

  adjustValueFieldType(key) {
    if (this.hasSettingValueTarget) {
      // Réinitialiser les classes et attributs
      this.settingValueTarget.classList.remove("h-32");

      // Ajuster en fonction du type de paramètre
      if (
        key.includes("_emails") ||
        key.includes("_recipients") ||
        key.includes("holidays")
      ) {
        // Champs qui nécessitent plus d'espace
        this.settingValueTarget.classList.add("h-32");
      }

      // Pour les valeurs booléennes, suggérer true/false
      if (key.includes("_enabled") || key.includes("_approval")) {
        this.settingValueTarget.setAttribute("placeholder", "true ou false");
      } else if (key.includes("_days")) {
        this.settingValueTarget.setAttribute(
          "placeholder",
          "ex: 1,2,3,4,5 pour lundi à vendredi",
        );
      } else if (key.includes("_hours")) {
        this.settingValueTarget.setAttribute("placeholder", "ex: 08:00-16:00");
      } else {
        this.settingValueTarget.setAttribute("placeholder", "");
      }
    }
  }

  /**
   * Gère la suppression d'un paramètre
   * @param {Event} event - L'événement de suppression
   */
  async removeSetting(event) {
    // Empêcher le comportement par défaut du lien
    event.preventDefault();

    const settingId = event.currentTarget.dataset.settingId;
    const href = event.currentTarget.getAttribute("href");
    const confirmMessage =
      event.currentTarget.dataset.confirmMessage ||
      "Êtes-vous sûr de vouloir supprimer ce paramètre ?";

    if (await dsConfirm(confirmMessage, { tone: "danger" })) {
      // Envoyer une requête AJAX pour supprimer le paramètre
      fetch(href, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            .content,
          Accept: "application/json",
        },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.success) {
            window.Notify.success(data.message);

            // Supprimer la ligne du tableau correspondant au paramètre supprimé
            const settingElement = document.getElementById(
              `school-setting-${settingId}`,
            );
            if (settingElement) {
              settingElement.remove();
            }

            // Si c'était le dernier paramètre d'une catégorie, masquer la catégorie
            const categories = document.querySelectorAll(".setting-category");
            categories.forEach((category) => {
              const settings = category.querySelectorAll(".setting-item");
              if (settings.length === 0) {
                category.style.display = "none";
              }
            });

            // Si c'était le dernier paramètre, afficher le message "aucun paramètre"
            const allSettings = document.querySelectorAll(".setting-item");
            if (allSettings.length === 0) {
              window.location.reload(); // Recharger la page pour afficher le message "aucun paramètre"
            }
          } else {
            window.Notify.error(
              data.message ||
                "Une erreur est survenue lors de la suppression du paramètre.",
            );
          }
        })
        .catch((error) => {
          console.error("Erreur lors de la suppression du paramètre:", error);
          window.Notify.error(
            "Une erreur est survenue lors de la suppression du paramètre.",
          );
        });
    }
  }
}
